import Add from "@mui/icons-material/Add";
import ArrowDownward from "@mui/icons-material/ArrowDownward";
import ArrowUpward from "@mui/icons-material/ArrowUpward";
import Check from "@mui/icons-material/Check";
import Delete from "@mui/icons-material/Delete";
import DragIndicator from "@mui/icons-material/DragIndicator";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { Box, Divider, IconButton, InputAdornment, MenuItem, Radio, Select, TextField, ToggleButton, Tooltip, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Reorder, useDragControls } from "framer-motion";
import { type FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "../../components/InHouseTooltip/InHouseTooltip";
import { GlobalLocationOrder } from "../../components/staffing/Header/GlobalLocationOrder";
import { DashedAddButton } from "../../components/UIToolkit/Clickable/DashedAddButton";
import { SystemSwitch } from "../../components/UIToolkit/Clickable/SystemSwitch";
import { SystemDropdown } from "../../components/UIToolkit/Inputs/SystemDropDown";
import { SystemValidationTextField } from "../../components/UIToolkit/Inputs/SystemValidationTextField";
import { CollapsibleSection } from "../../components/UIToolkit/Panels/CollapsibleSection";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useJobs } from "../../hooks/useJobs";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { NEUTRAL } from "../../theme/tokens";
import {
	type CustomSheetTab,
	type CustomSheetTabSelectionMode,
	type CustomSheetTabsConfig,
	DailyPrintoutGroupingOption,
	type PrintoutAdmin,
	PrintoutOrientation,
	type PrintoutShowAdminsConfig,
} from "../../utils/models";
import { AppendEmptyColumnsSetting } from "./AppendEmptyColumnsSetting";
import { AppendRoleToNameSetting } from "./AppendRoleToNameSetting";
import { CustomColumnNamesSetting } from "./CustomColumnNamesSetting";
import { CustomTimeGroupsSetting } from "./CustomTimeGroupsSetting";
import {
	AVAILABLE_COLUMN_TYPES,
	type PrintoutColumn,
	type PrintoutColumnType,
	type PrintoutSettings,
	type SortDirection,
	usePrintoutColumnLabel,
} from "./Header/usePrintout";
import { HideColumnNamesSetting } from "./HideColumnNamesSetting";
import { PrintoutAiAssist } from "./PrintoutAiAssist";
import { FitToPageSettings } from "./PrintoutSettingsControls";

const ADMIN_FIELD_MAX_LENGTH = 50;

const GROUPING_TO_COLUMN_TYPE: Record<string, PrintoutColumnType> = {
	[DailyPrintoutGroupingOption.LOCATION]: "location",
	[DailyPrintoutGroupingOption.START_TIME]: "shiftTime",
	[DailyPrintoutGroupingOption.ROLE]: "role",
	[DailyPrintoutGroupingOption.FLOOR]: "floor",
};

const COLUMN_TYPE_TO_GROUPING: Record<string, DailyPrintoutGroupingOption> = {
	location: DailyPrintoutGroupingOption.LOCATION,
	shiftTime: DailyPrintoutGroupingOption.START_TIME,
	role: DailyPrintoutGroupingOption.ROLE,
	floor: DailyPrintoutGroupingOption.FLOOR,
};

const GROUPING_COLUMN_TYPES: PrintoutColumnType[] = ["location", "shiftTime", "role", "floor"];

// Column types that can be used as the horizontal merge axis
const HORIZONTAL_MERGE_COLUMN_TYPES: PrintoutColumnType[] = ["certification", "role", "shiftTime", "floor", "location"];

type PrintoutSettingsPanelProps = {
	printoutSettings: PrintoutSettings;
	updateSetting: <K extends keyof PrintoutSettings>(key: K, value: PrintoutSettings[K]) => void;
	disabled?: boolean;
	enabledSections?: Set<PrintoutSettingsSection>;
	availableGroupingTypes?: PrintoutColumnType[];
	/**
	 * Applies a whole settings object at once. Passing it mounts the "Design with Annie" block above the
	 * panel — the caller decides where that belongs (Detailed only), so the panel never has to infer
	 * the printout type from `disabled`/`enabledSections`.
	 */
	onApplySettings?: (next: PrintoutSettings) => void;
	isDetailed: boolean;
};

const getNextSortDirection = (current?: SortDirection): SortDirection => (current === "desc" ? "asc" : "desc");

export type PrintoutSettingsSection = "toggle" | "grouping" | "order" | "layout" | "merge" | "horizontalMerge" | "summaryOptions";

const DEFAULT_PRINTOUT_SECTION_COLLAPSE = {
	toggle: false,
	grouping: false,
	order: false,
	layout: true,
	merge: false,
	horizontalMerge: false,
	summaryOptions: false,
} as const satisfies Record<PrintoutSettingsSection, boolean>;

const AddColumnButtons: FC<{
	existingColumns: PrintoutColumn[];
	onAdd: (col: PrintoutColumn) => void;
	availableTypes?: PrintoutColumnType[];
}> = ({ existingColumns, onAdd, availableTypes = AVAILABLE_COLUMN_TYPES }) => {
	const { t } = useTranslation();
	const getColumnLabel = usePrintoutColumnLabel();
	const available = availableTypes.filter((type) => !existingColumns.some((c) => c.type === type));
	if (available.length === 0) return null;

	return (
		<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
			{available.map((type) => (
				<Tooltip key={type} title={t("printout.add.column.tooltip", { column: getColumnLabel(type) })}>
					<IconButton
						size="small"
						onClick={() => onAdd({ type, enabled: true })}
						sx={{
							border: "1px dashed #ccc",
							borderRadius: 1,
							px: 1,
							py: 0.25,
							fontSize: "10px",
							color: "#666",
							"&:hover": { borderColor: "primary.dark", color: "primary.dark" },
						}}
					>
						<Add sx={{ fontSize: 14, mr: 0.25 }} />
						<Typography sx={{ fontSize: "10px" }}>{getColumnLabel(type)}</Typography>
					</IconButton>
				</Tooltip>
			))}
		</Box>
	);
};

const ColumnItem: FC<{
	column: PrintoutColumn;
	onToggle: () => void;
	onSortChange: (direction: SortDirection) => void;
	onRemove: () => void;
	showSortButton?: boolean;
	showRemoveButton?: boolean;
}> = ({ column, onToggle, onSortChange, onRemove, showSortButton = true, showRemoveButton = true }) => {
	const dragControls = useDragControls();
	const { t } = useTranslation();
	const getColumnLabel = usePrintoutColumnLabel();

	const displayName = getColumnLabel(column.type);

	return (
		<Reorder.Item value={column} dragListener={false} dragControls={dragControls} style={{ marginBottom: 8 }}>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 0.5,
					p: 1,
					borderRadius: 1,
					bgcolor: column.enabled ? (theme) => alpha(theme.palette.primary.main, 0.08) : NEUTRAL[100],
					opacity: column.enabled ? 1 : 0.6,
				}}
			>
				<Box onPointerDown={(e) => dragControls.start(e)} sx={{ cursor: "grab", display: "flex", alignItems: "center", color: "#999" }}>
					<DragIndicator fontSize="small" />
				</Box>

				<ToggleButton value="check" selected={column.enabled} onChange={onToggle} sx={{ width: "20px", height: "20px", minWidth: "20px", p: 0 }}>
					{column.enabled && <Check sx={{ fontSize: 14 }} />}
				</ToggleButton>

				<Typography sx={{ flex: 1, color: "primary.dark", fontWeight: 500, fontSize: "12px" }}>{displayName}</Typography>

				{showSortButton && (
					<Tooltip title={t("printout.column.sort.tooltip")} placement="top">
						<IconButton
							size="small"
							onClick={() => onSortChange(getNextSortDirection(column.sortDirection))}
							sx={{ width: 24, height: 24, color: "primary.dark" }}
						>
							{column.sortDirection === "desc" ? <ArrowDownward sx={{ fontSize: 16 }} /> : <ArrowUpward sx={{ fontSize: 16 }} />}
						</IconButton>
					</Tooltip>
				)}

				{showRemoveButton && (
					<Tooltip title={t("printout.column.remove")} placement="top">
						<IconButton size="small" onClick={onRemove} sx={{ width: 24, height: 24, color: "#999", "&:hover": { color: "#d32f2f" } }}>
							<Delete sx={{ fontSize: 16 }} />
						</IconButton>
					</Tooltip>
				)}
			</Box>
		</Reorder.Item>
	);
};

const CustomSheetTabsEditor: FC<{
	value: CustomSheetTabsConfig;
	onChange: (next: CustomSheetTabsConfig) => void;
	jobManagementEnabled: boolean;
}> = ({ value, onChange, jobManagementEnabled }) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const enabledJobs = useJobs();
	const tabs = value.tabs;
	const selectionMode: CustomSheetTabSelectionMode = jobManagementEnabled ? "jobs" : "role_groups";

	const availableOptions = useMemo<{ id: string; label: string }[]>(() => {
		if (jobManagementEnabled) {
			return [...enabledJobs].sort((a, b) => a.sortOrder - b.sortOrder).map((j) => ({ id: j.id, label: j.displayName || j.name }));
		}
		return Object.keys(chosenDepartment.role_groups ?? {}).map((g) => ({ id: g, label: g }));
	}, [jobManagementEnabled, enabledJobs, chosenDepartment.role_groups]);

	const labelFor = (id: string) => availableOptions.find((o) => o.id === id)?.label ?? id;

	const updateTab = (id: string, patch: Partial<CustomSheetTab>) => {
		onChange({ tabs: tabs.map((tab) => (tab.id === id ? { ...tab, ...patch } : tab)) });
	};
	const addTab = () => {
		onChange({
			tabs: [
				...tabs,
				{
					id: typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
					selectionMode,
					selectedKeys: [],
					breakByShift: false,
				},
			],
		});
	};
	const removeTab = (id: string) => {
		onChange({ tabs: tabs.filter((tab) => tab.id !== id) });
	};

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
			<Divider sx={{ my: 0.5 }} />
			<Typography sx={{ fontSize: "11px", color: "#8B8B8B", fontWeight: 500, textTransform: "uppercase", letterSpacing: 0.5 }}>
				{t("printout.custom.tabs.heading")}
			</Typography>

			{tabs.length === 0 && <Typography sx={{ fontSize: "11px", color: "#8B8B8B", fontStyle: "italic" }}>{t("printout.custom.empty.hint")}</Typography>}

			{tabs.map((tab) => (
				<Box
					key={tab.id}
					sx={{
						p: 1,
						borderRadius: 1,
						border: "1px solid #f0f0f0",
						bgcolor: "#fafafa",
						display: "flex",
						flexDirection: "column",
						gap: 0.75,
					}}
				>
					<SystemDropdown
						multiple
						size="small"
						displayEmpty
						items={availableOptions.map((o) => ({ value: o.id, label: o.label }))}
						selectedItems={tab.selectedKeys}
						onChangeMultiple={(values) => updateTab(tab.id, { selectedKeys: values })}
						valueToString={(v) => v}
						renderValue={(selected) => {
							const keys = Array.isArray(selected) ? selected : [selected];
							if (keys.length === 0) {
								return (
									<Typography sx={{ fontSize: "11px", color: "#8B8B8B" }}>
										{t(jobManagementEnabled ? "printout.custom.tab.jobs.placeholder" : "printout.custom.tab.role.groups.placeholder")}
									</Typography>
								);
							}
							return (
								<Typography sx={{ fontSize: "12px", color: "#4D5579", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
									{keys.map(labelFor).join(", ")}
								</Typography>
							);
						}}
						selectSx={{ fontSize: "12px", bgcolor: "white", width: "100%" }}
					/>

					<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
						<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
							<SystemSwitch
								checked={tab.breakByShift}
								onChange={() => updateTab(tab.id, { breakByShift: !tab.breakByShift })}
								label={t("printout.custom.tab.by.shift")}
							/>
							<InHouseTooltip title={t("printout.custom.tab.by.shift.tooltip")}>
								<InfoOutlined sx={{ fontSize: "16px", color: "#8B8B8B" }} />
							</InHouseTooltip>
						</Box>
						<Tooltip title={t("printout.custom.tab.remove")}>
							<IconButton size="small" onClick={() => removeTab(tab.id)} sx={{ p: 0.5 }}>
								<Delete sx={{ fontSize: "16px", color: "#999" }} />
							</IconButton>
						</Tooltip>
					</Box>
				</Box>
			))}

			<DashedAddButton dataTestId="printout-custom-tabs-add" label={t("printout.custom.add.tab")} onClick={addTab} disabled={false} />
		</Box>
	);
};

// Order matters: drives the radio layout in the Multi Tab section. Custom is last.
const MULTI_TAB_GROUP_BY_OPTIONS: { value: DailyPrintoutGroupingOption; labelKey: string; jobLabelKey?: string }[] = [
	{ value: DailyPrintoutGroupingOption.START_TIME, labelKey: "printout.option.shift" },
	{ value: DailyPrintoutGroupingOption.LOCATION, labelKey: "printout.option.location" },
	{ value: DailyPrintoutGroupingOption.ROLE, labelKey: "printout.option.role", jobLabelKey: "printout.option.job" },
	{ value: DailyPrintoutGroupingOption.FLOOR, labelKey: "printout.option.floor" },
	{ value: DailyPrintoutGroupingOption.CUSTOM, labelKey: "printout.option.custom" },
];

function getColumnLetter(columnNumber: number): string {
	if (columnNumber <= 0) return "";

	const remainder = (columnNumber - 1) % 26;
	const letter = String.fromCharCode(65 + remainder);
	const next = Math.floor((columnNumber - 1) / 26);

	return getColumnLetter(next) + letter;
}

const ShowAdminsSection: FC<{
	printoutSettings: PrintoutSettings;
	updateSetting: <K extends keyof PrintoutSettings>(key: K, value: PrintoutSettings[K]) => void;
}> = ({ printoutSettings, updateSetting }) => {
	const { t } = useTranslation();
	const config: PrintoutShowAdminsConfig = printoutSettings.showAdmins;

	const updateConfig = (next: PrintoutShowAdminsConfig) => updateSetting("showAdmins", next);

	const toggleEnabled = () => updateConfig({ ...config, enabled: !config.enabled });

	const addAdmin = () => updateConfig({ ...config, admins: [...config.admins, { name: "", role: "" }] });

	const updateAdmin = (idx: number, patch: Partial<PrintoutAdmin>) => {
		const admins = config.admins.map((a, i) => (i === idx ? { ...a, ...patch } : a));
		updateConfig({ ...config, admins });
	};

	const removeAdmin = (idx: number) => updateConfig({ ...config, admins: config.admins.filter((_, i) => i !== idx) });

	const isFieldValid = (value: string) => value.trim().length > 0 && value.length <= ADMIN_FIELD_MAX_LENGTH;

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
				<SystemSwitch checked={config.enabled} onChange={toggleEnabled} label={t("printout.show.admins")} />
				<InHouseTooltip title={t("printout.show.admins.tooltip")}>
					<InfoOutlined sx={{ fontSize: "18px" }} />
				</InHouseTooltip>
			</Box>
			{config.enabled && (
				<Box sx={{ display: "flex", flexDirection: "column", gap: 1, pl: 2, pt: 1 }}>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<SystemValidationTextField
							label={t("printout.show.admins.start.column")}
							value={String(config.startColumn)}
							onChange={(e) => updateConfig({ ...config, startColumn: Math.max(1, Number(e.target.value) || 1) })}
							inputType="number"
							reserveHeightForHelperText={false}
							formControlSx={{ width: 140 }}
							inputSx={{
								"& input": {
									color: "transparent",
									caretColor: "transparent",
									userSelect: "none",
								},
							}}
							injectedProps={{
								slotProps: {
									input: {
										startAdornment: (
											<InputAdornment position="start" sx={{ ml: 0, mr: 0, fontWeight: 600 }}>
												{getColumnLetter(config.startColumn)}
											</InputAdornment>
										),
									},
								},
							}}
							validation={{
								validationCallback: (v) => Number.isFinite(Number(v)) && Number(v) >= 1,
								performValidation: "onlyAfterInitialChange",
								showValidWhenValid: "never",
							}}
						/>
						<SystemValidationTextField
							label={t("printout.show.admins.pair.spacing")}
							value={String(config.pairSpacing)}
							onChange={(e) => updateConfig({ ...config, pairSpacing: Math.max(0, Number(e.target.value) || 0) })}
							inputType="number"
							reserveHeightForHelperText={false}
							formControlSx={{ width: 120 }}
							validation={{
								validationCallback: (v) => Number.isFinite(Number(v)) && Number(v) >= 0,
								performValidation: "onlyAfterInitialChange",
								showValidWhenValid: "never",
							}}
						/>
					</Box>
					{config.admins.map((admin, idx) => (
						// biome-ignore lint/suspicious/noArrayIndexKey: list is reorder-free; identity is positional
						<Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<SystemValidationTextField
								value={admin.role}
								onChange={(e) => updateAdmin(idx, { role: e.target.value.slice(0, ADMIN_FIELD_MAX_LENGTH) })}
								placeholder={t("printout.show.admins.role")}
								reserveHeightForHelperText={false}
								formControlSx={{ flex: 1 }}
								validation={{
									validationCallback: isFieldValid,
									performValidation: "onlyAfterInitialChange",
									showValidWhenValid: "never",
								}}
							/>
							<SystemValidationTextField
								value={admin.name}
								onChange={(e) => updateAdmin(idx, { name: e.target.value.slice(0, ADMIN_FIELD_MAX_LENGTH) })}
								placeholder={t("printout.show.admins.name")}
								reserveHeightForHelperText={false}
								formControlSx={{ flex: 1 }}
								validation={{
									validationCallback: isFieldValid,
									performValidation: "onlyAfterInitialChange",
									showValidWhenValid: "never",
								}}
							/>
							<IconButton size="small" onClick={() => removeAdmin(idx)} aria-label={t("delete")}>
								<Delete fontSize="small" />
							</IconButton>
						</Box>
					))}
					<Box sx={{ display: "flex" }}>
						<IconButton size="small" onClick={addAdmin} aria-label={t("printout.show.admins.add")}>
							<Add fontSize="small" />
						</IconButton>
						<Typography sx={{ fontSize: "12px", color: "primary.dark", alignSelf: "center" }}>{t("printout.show.admins.add")}</Typography>
					</Box>
				</Box>
			)}
		</Box>
	);
};

const MultiTabSection: FC<{
	printoutSettings: PrintoutSettings;
	updateSetting: <K extends keyof PrintoutSettings>(key: K, value: PrintoutSettings[K]) => void;
	jobManagementEnabled: boolean;
}> = ({ printoutSettings, updateSetting, jobManagementEnabled }) => {
	const { t } = useTranslation();
	const isMultiTab = printoutSettings.separateSheetsBy !== DailyPrintoutGroupingOption.NONE;
	// Remember the last non-NONE choice so toggling Multi Tab off → on doesn't force the user
	// to re-pick a Group By value.
	const [lastMode, setLastMode] = useState<DailyPrintoutGroupingOption>(
		isMultiTab ? printoutSettings.separateSheetsBy : DailyPrintoutGroupingOption.START_TIME,
	);

	const handleToggleMultiTab = () => {
		if (isMultiTab) {
			setLastMode(printoutSettings.separateSheetsBy);
			updateSetting("separateSheetsBy", DailyPrintoutGroupingOption.NONE);
		} else {
			updateSetting("separateSheetsBy", lastMode);
		}
	};

	const handleSelectMode = (mode: DailyPrintoutGroupingOption) => {
		setLastMode(mode);
		updateSetting("separateSheetsBy", mode);
	};

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
			<SystemSwitch checked={isMultiTab} onChange={handleToggleMultiTab} label={t("printout.multitab.toggle")} />
			{isMultiTab && (
				<Box>
					<Typography sx={{ fontSize: "11px", color: "#8B8B8B", fontWeight: 500, mb: 1, textTransform: "uppercase", letterSpacing: 0.5 }}>
						{t("printout.multitab.group.by")}
					</Typography>
					<Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", rowGap: 0.5, columnGap: 1 }}>
						{MULTI_TAB_GROUP_BY_OPTIONS.map(({ value, labelKey, jobLabelKey }) => {
							const labelToUse = jobManagementEnabled && jobLabelKey ? jobLabelKey : labelKey;
							return (
								<Box
									key={value}
									onClick={() => handleSelectMode(value)}
									sx={{ display: "flex", alignItems: "center", gap: 0.25, cursor: "pointer", userSelect: "none" }}
								>
									<Radio checked={printoutSettings.separateSheetsBy === value} size="small" sx={{ p: 0.5 }} />
									<Typography sx={{ fontSize: "12px", color: "#4D5579" }}>{t(labelToUse)}</Typography>
								</Box>
							);
						})}
					</Box>

					{printoutSettings.separateSheetsBy === DailyPrintoutGroupingOption.CUSTOM && (
						<CustomSheetTabsEditor
							value={printoutSettings.customSheetTabs}
							onChange={(next) => updateSetting("customSheetTabs", next)}
							jobManagementEnabled={jobManagementEnabled}
						/>
					)}
				</Box>
			)}
		</Box>
	);
};

export const PrintoutSettingsPanel: FC<PrintoutSettingsPanelProps> = ({
	printoutSettings,
	updateSetting,
	disabled,
	enabledSections,
	availableGroupingTypes,
	onApplySettings,
	isDetailed,
}) => {
	const isSectionDisabled = (section: PrintoutSettingsSection) => disabled && !enabledSections?.has(section);
	const effectiveGroupingTypes = availableGroupingTypes ?? GROUPING_COLUMN_TYPES;
	const { t } = useTranslation();
	const theme = useTheme();
	const summaryLabelFontSize = theme.typography.caption.fontSize;
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const getColumnLabel = usePrintoutColumnLabel();
	const [sectionOpen, setSectionOpen] = useState<Record<PrintoutSettingsSection, boolean>>(DEFAULT_PRINTOUT_SECTION_COLLAPSE);

	const toggle = (section: PrintoutSettingsSection) => setSectionOpen((prev) => ({ ...prev, [section]: !prev[section] }));

	const setColumnGroupingBy = (value: PrintoutColumnType[] | null) => updateSetting("columnGroupingBy", value);

	const [groupingColumns, setGroupingColumns] = useState<PrintoutColumn[]>(() =>
		printoutSettings.dailyPrintoutGrouping.map((opt) => ({ type: GROUPING_TO_COLUMN_TYPE[opt], enabled: true })),
	);
	useEffect(() => {
		const settingsEnabled = new Set(printoutSettings.dailyPrintoutGrouping.map((opt) => GROUPING_TO_COLUMN_TYPE[opt]));
		const localEnabled = new Set(groupingColumns.filter((c) => c.enabled).map((c) => c.type));
		const same = settingsEnabled.size === localEnabled.size && [...settingsEnabled].every((t) => localEnabled.has(t));
		if (!same) {
			// sync local state with settings if they differ - this handles the case where user changes the grouping option that isn't in the current local state
			setGroupingColumns(printoutSettings.dailyPrintoutGrouping.map((opt) => ({ type: GROUPING_TO_COLUMN_TYPE[opt], enabled: true })));
		}
	}, [printoutSettings.dailyPrintoutGrouping, groupingColumns]);

	const updateGroupingColumns = (updated: PrintoutColumn[]) => {
		setGroupingColumns(updated);
		updateSetting(
			"dailyPrintoutGrouping",
			updated.filter((c) => c.enabled).map((c) => COLUMN_TYPE_TO_GROUPING[c.type]),
		);
	};

	const [rowGroupingByState, setRowGroupingByState] = useState<PrintoutColumn[]>(() => printoutSettings.rowGroupingBy.map((type) => ({ type, enabled: true })));
	useEffect(() => {
		const settingsEnabled = new Set(printoutSettings.rowGroupingBy);
		const localEnabled = new Set(rowGroupingByState.filter((c) => c.enabled).map((c) => c.type));
		const same = settingsEnabled.size === localEnabled.size && [...settingsEnabled].every((t) => localEnabled.has(t));
		if (!same) {
			// sync local state with settings if they differ - this handles the case where user changes the merge option that isn't in the current local state
			setRowGroupingByState(printoutSettings.rowGroupingBy.map((type) => ({ type, enabled: true })));
		}
	}, [printoutSettings.rowGroupingBy, rowGroupingByState]);

	const updateRowGroupingBy = (updated: PrintoutColumn[]) => {
		setRowGroupingByState(updated);
		updateSetting(
			"rowGroupingBy",
			updated.filter((c) => c.enabled).map((c) => c.type),
		);
	};

	const mergeAvailableTypes = useMemo<PrintoutColumnType[]>(
		() => printoutSettings.columns.filter((c) => c.enabled && !rowGroupingByState.some((mc) => mc.type === c.type)).map((c) => c.type),
		[printoutSettings.columns, rowGroupingByState],
	);

	const shiftTimeActive = useMemo(
		() =>
			printoutSettings.dailyPrintoutGrouping.includes(DailyPrintoutGroupingOption.START_TIME) ||
			printoutSettings.separateSheetsBy === DailyPrintoutGroupingOption.START_TIME,
		[printoutSettings.dailyPrintoutGrouping, printoutSettings.separateSheetsBy],
	);
	// Custom shift time groups decide every Start Time section and tab, so aligning to popular times has nothing left to do.
	const customTimeGroupsOverrideAlign = isDetailed && printoutSettings.customTimeGroups.enabled;
	const alignDisabledTooltipKey = customTimeGroupsOverrideAlign
		? "printout.align.group.times.to.popular.custom.groups.tooltip"
		: "printout.align.group.times.to.popular.disabled.tooltip";

	return (
		<Box
			sx={{
				height: "100%",
				border: "1px solid #e0e0e0",
				borderRadius: 1,
				p: 2,
				overflowY: "auto",
			}}
		>
			{!!onApplySettings && <PrintoutAiAssist printoutSettings={printoutSettings} onApplySettings={onApplySettings} />}

			<Typography sx={{ color: "primary.dark", fontWeight: 600, fontSize: "18px", mb: 2 }}>{t("printout.settings")}</Typography>

			{/* Summary-mode-only options. `enabledSections` is only defined in summary mode, so this section
			    never renders in detailed / 530 / spreadsheet, and stays interactive despite the panel-wide disable. */}
			{enabledSections?.has("summaryOptions") && (
				<>
					<Box>
						<CollapsibleSection title={t("printout.summary.options")} open={sectionOpen.summaryOptions} onToggle={() => toggle("summaryOptions")}>
							<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
								<SystemSwitch
									dataTestId="printout-summary-options-show-dhppd"
									checked={printoutSettings.showDhppd}
									onChange={() => updateSetting("showDhppd", !printoutSettings.showDhppd)}
									label={t("printout.show.dhppd")}
									labelFontSize={summaryLabelFontSize}
								/>
								<SystemSwitch
									dataTestId="printout-summary-options-show-nhppd"
									checked={printoutSettings.showNhppd}
									onChange={() => updateSetting("showNhppd", !printoutSettings.showNhppd)}
									label={t("printout.show.nhppd")}
									labelFontSize={summaryLabelFontSize}
								/>
								{/* Only meaningful for DHPPD/NHPPD — the default HPPD row mirrors the Day View value (trainees
								    excluded), so this toggle is disabled until one of those variants is shown. */}
								<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
									<SystemSwitch
										dataTestId="printout-summary-options-hppd-include-trainees"
										checked={printoutSettings.hppdIncludeTrainees}
										onChange={() => updateSetting("hppdIncludeTrainees", !printoutSettings.hppdIncludeTrainees)}
										label={t("printout.hppd.include.trainees")}
										labelFontSize={summaryLabelFontSize}
										disabled={!(printoutSettings.showDhppd || printoutSettings.showNhppd)}
									/>
									<InHouseTooltip
										title={t(
											printoutSettings.showDhppd || printoutSettings.showNhppd
												? "printout.hppd.include.trainees.tooltip"
												: "printout.hppd.include.trainees.disabled.tooltip",
										)}
									>
										<InfoOutlined sx={{ fontSize: "18px" }} />
									</InHouseTooltip>
								</Box>
								<SystemSwitch
									dataTestId="printout-summary-options-show-actual-staff-column"
									checked={printoutSettings.showActualStaffColumn}
									onChange={() => updateSetting("showActualStaffColumn", !printoutSettings.showActualStaffColumn)}
									label={t("printout.show.actual.staff.column")}
									labelFontSize={summaryLabelFontSize}
								/>
								<SystemSwitch
									dataTestId="printout-summary-options-show-actual-hours-column"
									checked={printoutSettings.showActualHoursColumn}
									onChange={() => updateSetting("showActualHoursColumn", !printoutSettings.showActualHoursColumn)}
									label={t("printout.show.actual.hours.column")}
									labelFontSize={summaryLabelFontSize}
								/>
							</Box>
						</CollapsibleSection>
					</Box>
					<Divider sx={{ my: 1 }} />
				</>
			)}

			{/* Columns */}
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					gap: 1,
					opacity: isSectionDisabled("layout") ? 0.5 : 1,
					pointerEvents: isSectionDisabled("layout") ? "none" : "auto",
				}}
			>
				<CollapsibleSection title={t("printout.columns.layout")} open={sectionOpen.layout} onToggle={() => toggle("layout")}>
					<Typography sx={{ color: "#8B8B8B", fontSize: "11px", mb: 1 }}>{t("printout.columns.drag.hint")}</Typography>

					<Reorder.Group
						axis="y"
						values={printoutSettings.columns}
						onReorder={(newColumns) => updateSetting("columns", newColumns)}
						style={{ listStyle: "none", padding: 0, margin: 0 }}
					>
						{printoutSettings.columns.map((column) => (
							<ColumnItem
								key={column.type}
								column={column}
								showSortButton={false}
								onToggle={() => {
									const updated = printoutSettings.columns.map((c) => (c.type === column.type ? { ...c, enabled: !c.enabled } : c));
									updateSetting("columns", updated);
									if (!updated.find((c) => c.type === column.type)?.enabled) {
										updateSetting(
											"rowGroupingBy",
											printoutSettings.rowGroupingBy.filter((t) => t !== column.type),
										);
									}
								}}
								onSortChange={() => {}}
								onRemove={() => {
									const updated = printoutSettings.columns.filter((c) => c.type !== column.type);
									updateSetting("columns", updated);
									updateSetting(
										"rowGroupingBy",
										printoutSettings.rowGroupingBy.filter((t) => t !== column.type),
									);
								}}
							/>
						))}
					</Reorder.Group>

					<AddColumnButtons existingColumns={printoutSettings.columns} onAdd={(col) => updateSetting("columns", [...printoutSettings.columns, col])} />

					{isDetailed && (
						<Box sx={{ mt: 1.5 }}>
							<CustomColumnNamesSetting
								isDetailed={isDetailed}
								columns={printoutSettings.columns}
								customColumnNames={printoutSettings.customColumnNames}
								columnLabel={getColumnLabel}
								onCustomColumnNamesChange={(next) => updateSetting("customColumnNames", next)}
							/>
							<CustomTimeGroupsSetting
								isDetailed={isDetailed}
								customTimeGroups={printoutSettings.customTimeGroups}
								onCustomTimeGroupsChange={(next) => updateSetting("customTimeGroups", next)}
							/>
						</Box>
					)}

					{/* Staff Per Row */}
					<Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5 }}>
						<Typography sx={{ fontSize: "12px", color: "primary.dark", fontWeight: 500, flex: 1 }}>{t("printout.staff.per.row")}</Typography>
						<TextField
							type="number"
							size="small"
							value={printoutSettings.staffPerRow}
							onChange={(e) => {
								const val = Math.max(1, parseInt(e.target.value, 10) || 1);
								updateSetting("staffPerRow", val);
							}}
							slotProps={{ htmlInput: { min: 1 } }}
							sx={{ width: 80 }}
						/>
					</Box>
				</CollapsibleSection>
			</Box>

			<Divider sx={{ my: 1 }} />

			{/* Sections */}
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					gap: 1,
					opacity: isSectionDisabled("grouping") ? 0.5 : 1,
					pointerEvents: isSectionDisabled("grouping") ? "none" : "auto",
				}}
			>
				<CollapsibleSection title={t("printout.grouped.by")} open={sectionOpen.grouping} onToggle={() => toggle("grouping")}>
					<Typography sx={{ color: "#8B8B8B", fontSize: "11px", mb: 1 }}>{t("printout.grouped.by.hint")}</Typography>

					<Reorder.Group axis="y" values={groupingColumns} onReorder={updateGroupingColumns} style={{ listStyle: "none", padding: 0, margin: 0 }}>
						{groupingColumns
							.filter((c) => effectiveGroupingTypes.includes(c.type))
							.map((column) => (
								<ColumnItem
									key={column.type}
									column={column}
									showSortButton={false}
									onToggle={() => updateGroupingColumns(groupingColumns.map((c) => (c.type === column.type ? { ...c, enabled: !c.enabled } : c)))}
									onSortChange={() => {}}
									onRemove={() => updateGroupingColumns(groupingColumns.filter((c) => c.type !== column.type))}
								/>
							))}
					</Reorder.Group>

					<AddColumnButtons
						existingColumns={groupingColumns}
						availableTypes={effectiveGroupingTypes}
						onAdd={(col) => updateGroupingColumns([...groupingColumns, col])}
					/>
				</CollapsibleSection>
			</Box>

			<Divider sx={{ my: 1 }} />

			{/* Vertical Merge */}
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					gap: 1,
					opacity: isSectionDisabled("merge") ? 0.5 : 1,
					pointerEvents: isSectionDisabled("merge") ? "none" : "auto",
				}}
			>
				<CollapsibleSection title={t("printout.merge.cells.by")} open={sectionOpen.merge} onToggle={() => toggle("merge")}>
					<Typography sx={{ color: "#8B8B8B", fontSize: "11px", mb: 1 }}>{t("printout.merge.cells.by.hint")}</Typography>

					{rowGroupingByState.map((column) => (
						<Box
							key={column.type}
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 0.5,
								p: 1,
								mb: 1,
								borderRadius: 1,
								bgcolor: column.enabled ? (theme) => alpha(theme.palette.primary.main, 0.08) : NEUTRAL[100],
								opacity: column.enabled ? 1 : 0.6,
							}}
						>
							<ToggleButton
								value="check"
								selected={column.enabled}
								onChange={() => updateRowGroupingBy(rowGroupingByState.map((c) => (c.type === column.type ? { ...c, enabled: !c.enabled } : c)))}
								sx={{ width: "20px", height: "20px", minWidth: "20px", p: 0 }}
							>
								{column.enabled && <Check sx={{ fontSize: 14 }} />}
							</ToggleButton>
							<Typography sx={{ flex: 1, color: "primary.dark", fontWeight: 500, fontSize: "12px" }}>{getColumnLabel(column.type)}</Typography>
							<Tooltip title={t("printout.column.remove")} placement="top">
								<IconButton
									size="small"
									onClick={() => updateRowGroupingBy(rowGroupingByState.filter((c) => c.type !== column.type))}
									sx={{ width: 24, height: 24, color: "#999", "&:hover": { color: "#d32f2f" } }}
								>
									<Delete sx={{ fontSize: 16 }} />
								</IconButton>
							</Tooltip>
						</Box>
					))}

					<AddColumnButtons
						existingColumns={rowGroupingByState}
						availableTypes={mergeAvailableTypes}
						onAdd={(col) => updateRowGroupingBy([...rowGroupingByState, col])}
					/>
				</CollapsibleSection>
			</Box>

			<Divider sx={{ my: 1 }} />

			{/* Horizontal Merge */}
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					gap: 1,
					opacity: isSectionDisabled("horizontalMerge") ? 0.5 : 1,
					pointerEvents: isSectionDisabled("horizontalMerge") ? "none" : "auto",
				}}
			>
				<CollapsibleSection title={t("printout.horizontal.merge")} open={sectionOpen.horizontalMerge} onToggle={() => toggle("horizontalMerge")}>
					<Typography sx={{ color: "#8B8B8B", fontSize: "11px", mb: 1 }}>{t("printout.horizontal.merge.hint")}</Typography>

					{/* None option */}
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 0.5,
							p: 1,
							mb: 1,
							borderRadius: 1,
							bgcolor: printoutSettings.columnGroupingBy ? (theme) => alpha(theme.palette.primary.main, 0.08) : NEUTRAL[100],
							cursor: "pointer",
						}}
						onClick={() => setColumnGroupingBy([])}
					>
						<ToggleButton
							value="check"
							selected={!printoutSettings.columnGroupingBy || printoutSettings.columnGroupingBy.length === 0}
							onChange={() => setColumnGroupingBy([])}
							sx={{ width: "20px", height: "20px", minWidth: "20px", p: 0 }}
						>
							{(!printoutSettings.columnGroupingBy || printoutSettings.columnGroupingBy.length === 0) && <Check sx={{ fontSize: 14 }} />}
						</ToggleButton>
						<Typography sx={{ flex: 1, color: "primary.dark", fontWeight: 500, fontSize: "12px" }}>{t("printout.horizontal.merge.none")}</Typography>
					</Box>

					{/* One option per horizontal merge column type that is currently enabled in the layout */}
					{HORIZONTAL_MERGE_COLUMN_TYPES.filter((type) => printoutSettings.columns.some((c) => c.type === type && c.enabled)).map((type) => (
						<Box
							key={type}
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 0.5,
								p: 1,
								mb: 1,
								borderRadius: 1,
								bgcolor: printoutSettings.columnGroupingBy?.[0] === type ? (theme) => alpha(theme.palette.primary.main, 0.08) : NEUTRAL[100],
								cursor: "pointer",
							}}
							onClick={() => setColumnGroupingBy([type])}
						>
							<ToggleButton
								value="check"
								selected={printoutSettings.columnGroupingBy?.[0] === type}
								onChange={() => setColumnGroupingBy([type])}
								sx={{ width: "20px", height: "20px", minWidth: "20px", p: 0 }}
							>
								{printoutSettings.columnGroupingBy?.[0] === type && <Check sx={{ fontSize: 14 }} />}
							</ToggleButton>
							<Typography sx={{ flex: 1, color: "primary.dark", fontWeight: 500, fontSize: "12px" }}>{getColumnLabel(type)}</Typography>
						</Box>
					))}
				</CollapsibleSection>
			</Box>

			<Divider sx={{ my: 1 }} />

			{/* Sort */}
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					gap: 1,
					opacity: isSectionDisabled("order") ? 0.5 : 1,
					pointerEvents: isSectionDisabled("order") ? "none" : "auto",
				}}
			>
				<CollapsibleSection title={t("printout.order.by")} open={sectionOpen.order} onToggle={() => toggle("order")}>
					<Typography sx={{ color: "#8B8B8B", fontSize: "11px", mb: 1 }}>{t("printout.order.by.hint")}</Typography>

					<Reorder.Group
						axis="y"
						values={printoutSettings.sortBy}
						onReorder={(newSortBy) => updateSetting("sortBy", newSortBy)}
						style={{ listStyle: "none", padding: 0, margin: 0 }}
					>
						{printoutSettings.sortBy.map((column) => (
							<ColumnItem
								key={column.type}
								column={column}
								onToggle={() => {
									const updated = printoutSettings.sortBy.map((c) => (c.type === column.type ? { ...c, enabled: !c.enabled } : c));
									updateSetting("sortBy", updated);
								}}
								onSortChange={(direction) => {
									const updated = printoutSettings.sortBy.map((c) => (c.type === column.type ? { ...c, sortDirection: direction } : c));
									updateSetting("sortBy", updated);
								}}
								onRemove={() => {
									const updated = printoutSettings.sortBy.filter((c) => c.type !== column.type);
									updateSetting("sortBy", updated);
								}}
							/>
						))}
					</Reorder.Group>

					<AddColumnButtons
						existingColumns={printoutSettings.sortBy}
						onAdd={(col) => updateSetting("sortBy", [{ ...col, sortDirection: "asc" }, ...printoutSettings.sortBy])}
					/>
				</CollapsibleSection>
			</Box>

			<Divider sx={{ my: 1 }} />

			{/* Additional Settings */}
			<Box sx={{ opacity: isSectionDisabled("toggle") ? 0.5 : 1, pointerEvents: isSectionDisabled("toggle") ? "none" : "auto" }}>
				<CollapsibleSection title={t("printout.toggle.settings")} open={sectionOpen.toggle} onToggle={() => toggle("toggle")}>
					<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
						<AppendRoleToNameSetting
							isDetailed={isDetailed}
							appendRoleToName={printoutSettings.appendRoleToName}
							onAppendRoleToNameChange={(enabled) => updateSetting("appendRoleToName", enabled)}
						/>
						<HideColumnNamesSetting
							isDetailed={isDetailed}
							hideColumnNames={printoutSettings.hideColumnNames}
							onHideColumnNamesChange={(enabled) => updateSetting("hideColumnNames", enabled)}
						/>
						<AppendEmptyColumnsSetting
							isDetailed={isDetailed}
							appendEmptyColumns={printoutSettings.appendEmptyColumns}
							onAppendEmptyColumnsChange={(count) => updateSetting("appendEmptyColumns", count)}
						/>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<SystemSwitch
								checked={printoutSettings.printGaps}
								onChange={() => updateSetting("printGaps", !printoutSettings.printGaps)}
								label={t("printout.show.gaps")}
							/>
							<InHouseTooltip title={t("printout.show.gaps.tooltip")}>
								<InfoOutlined sx={{ fontSize: "18px" }} />
							</InHouseTooltip>
						</Box>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<SystemSwitch
								checked={printoutSettings.showRoleGroupStats}
								onChange={() => updateSetting("showRoleGroupStats", !printoutSettings.showRoleGroupStats)}
								label={t(jobManagementEnabled ? "printout.show.job.group.stats" : "printout.show.role.group.stats")}
							/>
							<InHouseTooltip title={t(jobManagementEnabled ? "printout.show.job.group.stats.tooltip" : "printout.show.role.group.stats.tooltip")}>
								<InfoOutlined sx={{ fontSize: "18px" }} />
							</InHouseTooltip>
						</Box>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<SystemSwitch
								checked={printoutSettings.showPtoSheet}
								onChange={() => updateSetting("showPtoSheet", !printoutSettings.showPtoSheet)}
								label={t("printout.show.pto")}
							/>
							<InHouseTooltip title={t("printout.show.pto.tooltip")}>
								<InfoOutlined sx={{ fontSize: "18px" }} />
							</InHouseTooltip>
						</Box>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1, pl: 2, opacity: printoutSettings.showPtoSheet ? 1 : 0.4 }}>
							<SystemSwitch
								checked={printoutSettings.includeTimeOffReason}
								onChange={() => updateSetting("includeTimeOffReason", !printoutSettings.includeTimeOffReason)}
								label={t("printout.include.time.off.reason")}
								disabled={!printoutSettings.showPtoSheet}
							/>
						</Box>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<SystemSwitch
								checked={printoutSettings.showCallOutsSheet}
								onChange={() => updateSetting("showCallOutsSheet", !printoutSettings.showCallOutsSheet)}
								label={t("printout.show.call.outs")}
								dataTestId="printout-show-call-outs-switch"
							/>
							<InHouseTooltip title={t("printout.show.call.outs.tooltip")}>
								<InfoOutlined sx={{ fontSize: "18px" }} />
							</InHouseTooltip>
						</Box>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<SystemSwitch
								checked={printoutSettings.divideGroupsByPage}
								onChange={() => updateSetting("divideGroupsByPage", !printoutSettings.divideGroupsByPage)}
								label={t("printout.divide.groups.by.page")}
							/>
							<InHouseTooltip title={t("printout.divide.groups.by.page.tooltip")}>
								<InfoOutlined sx={{ fontSize: "18px" }} />
							</InHouseTooltip>
						</Box>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<SystemSwitch
								checked={printoutSettings.spaceBetweenGroups}
								onChange={() => updateSetting("spaceBetweenGroups", !printoutSettings.spaceBetweenGroups)}
								label={t("printout.space.between.groups")}
							/>
							<InHouseTooltip title={t("printout.space.between.groups.tooltip")}>
								<InfoOutlined sx={{ fontSize: "18px" }} />
							</InHouseTooltip>
						</Box>

						<SystemSwitch
							checked={printoutSettings.flagNotOnboarded}
							onChange={() => updateSetting("flagNotOnboarded", !printoutSettings.flagNotOnboarded)}
							label={t("printout.flag.not.onboarded")}
						/>

						<SystemSwitch
							checked={printoutSettings.flagAgency}
							onChange={() => updateSetting("flagAgency", !printoutSettings.flagAgency)}
							label={t("printout.flag.agency")}
						/>

						<SystemSwitch
							checked={printoutSettings.hideHppdNursingHours}
							onChange={() => updateSetting("hideHppdNursingHours", !printoutSettings.hideHppdNursingHours)}
							label={t("printout.hide.hppd.nursing.hours")}
						/>

						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<SystemSwitch
								checked={printoutSettings.mergeSimilarShiftTimes}
								onChange={() => updateSetting("mergeSimilarShiftTimes", !printoutSettings.mergeSimilarShiftTimes)}
								label={t("printout.merge.similar.shift.times")}
							/>
							<InHouseTooltip title={t("printout.merge.similar.shift.times.tooltip")}>
								<InfoOutlined sx={{ fontSize: "18px" }} />
							</InHouseTooltip>
						</Box>

						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							{/* Only meaningful when grouping OR splitting tabs by Shift Time without custom shift time
						    groups — disabled otherwise, with the tooltip explaining why. */}
							<SystemSwitch
								checked={printoutSettings.alignGroupTimesToPopular}
								onChange={() => updateSetting("alignGroupTimesToPopular", !printoutSettings.alignGroupTimesToPopular)}
								label={t("printout.align.group.times.to.popular")}
								disabled={!shiftTimeActive || customTimeGroupsOverrideAlign}
								dataTestId="printout-align-group-times-to-popular-switch"
							/>
							<InHouseTooltip
								title={t(shiftTimeActive && !customTimeGroupsOverrideAlign ? "printout.align.group.times.to.popular.tooltip" : alignDisabledTooltipKey)}
							>
								<InfoOutlined sx={{ fontSize: "18px" }} />
							</InHouseTooltip>
						</Box>

						<ShowAdminsSection printoutSettings={printoutSettings} updateSetting={updateSetting} />

						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							{/* Show per-location census next to location section/column labels */}
							<SystemSwitch
								checked={printoutSettings.showLocationCensus}
								onChange={() => updateSetting("showLocationCensus", !printoutSettings.showLocationCensus)}
								label={t("printout.show.location.census")}
								dataTestId="printout-show-location-census-switch"
							/>
							<InHouseTooltip title={t("printout.show.location.census.tooltip")}>
								<InfoOutlined sx={{ fontSize: "18px" }} />
							</InHouseTooltip>
						</Box>

						{/* Scale to fit (Excel page setup) */}
						<FitToPageSettings
							fitToPage={printoutSettings.fitToPage}
							fitToWidth={printoutSettings.fitToWidth}
							fitToHeight={printoutSettings.fitToHeight}
							onFitToPageChange={(value) => updateSetting("fitToPage", value)}
							onFitToWidthChange={(value) => updateSetting("fitToWidth", value)}
							onFitToHeightChange={(value) => updateSetting("fitToHeight", value)}
							showPageCounts
						/>

						{/* Orientation */}
						<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
							<Typography sx={{ fontSize: "12px", color: "primary.dark", fontWeight: 500 }}>{t("printout.orientation")}</Typography>
							<Select
								size="small"
								value={printoutSettings.orientation}
								onChange={(e) => updateSetting("orientation", e.target.value as PrintoutOrientation)}
								sx={{ fontSize: "12px", minWidth: 110 }}
							>
								<MenuItem value={PrintoutOrientation.Landscape}>{t("printout.orientation.landscape")}</MenuItem>
								<MenuItem value={PrintoutOrientation.Portrait}>{t("printout.orientation.portrait")}</MenuItem>
							</Select>
						</Box>

						<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
							<Typography sx={{ fontSize: "12px", color: "primary.dark", fontWeight: 500 }}>{t("location.order")}</Typography>
							<GlobalLocationOrder />
						</Box>
					</Box>
				</CollapsibleSection>
			</Box>

			<Divider sx={{ my: 1 }} />

			{/* Multi Tab — flat (no CollapsibleSection): the toggle itself is the entry point. */}
			<MultiTabSection printoutSettings={printoutSettings} updateSetting={updateSetting} jobManagementEnabled={jobManagementEnabled} />
		</Box>
	);
};
